import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import resizeWorker from 'store/resizeWorker';
import { errorMessage } from './errorMessage';
import { SlotBudget, fitsSlotBudget, slotBudgetHint } from 'utils/slotBudget';

interface ResizeWorkerDialogProps {
  workerId: string;
  workerName: string;
  currentCores: number;
  heldCores?: number;
  replacing?: string[];
  // The node's slot budget, when it has one: growth past it is checked before submit.
  slotBudget?: SlotBudget | null;
  onClose: () => void;
  onConfirm: (cores: number) => void;
}

export default function ResizeWorkerDialog({
  workerId,
  workerName,
  currentCores,
  heldCores,
  replacing,
  slotBudget,
  onClose,
  onConfirm,
}: ResizeWorkerDialogProps) {
  const isReplacing = (replacing?.length ?? 0) > 0;
  const [cores, setCores] = useState(String(currentCores));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalized = cores.trim();
  const valid = /^\d+$/.test(normalized);
  const parsed = valid ? Number(normalized) : Number.NaN;
  const fits = !valid || fitsSlotBudget(slotBudget ?? null, parsed, currentCores);
  const budgetHint = valid ? slotBudgetHint(slotBudget ?? null, parsed, currentCores) : null;

  const handleConfirm = async () => {
    if (isSubmitting || !valid) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await resizeWorker(workerId, parsed);
      onConfirm(parsed);
      onClose();
    } catch (err) {
      console.error('Failed to resize worker:', err);
      setError(`Failed to resize worker: ${await errorMessage(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resize Worker</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          {isReplacing ? (
            <>
              Set the slot (core) budget for worker <strong>{workerName}</strong>. It is taking over from{' '}
              {replacing!.join(', ')} and holds {heldCores ?? 0} of its {currentCores} slots so far; the rest move here
              as the workers it replaces finish their running tasks. The budget is the size the group ends up at.
            </>
          ) : (
            <>
              Set the number of slots (cores) for worker <strong>{workerName}</strong>.
            </>
          )}
          <br />
          Scaling down drains surplus slots: running tasks finish first, then the slots are released (no work is lost).
          Setting <strong>0</strong> parks the worker as a warm standby — it keeps running but takes no new tasks until
          you scale it back up.
        </DialogContentText>
        <TextField
          label="Cores"
          type="number"
          value={cores}
          onChange={(e) => setCores(e.target.value)}
          fullWidth
          margin="normal"
          slotProps={{ htmlInput: { min: 0 } }}
          error={!valid || !fits}
          helperText={
            !valid
              ? 'Enter a whole number of cores (0 or more).'
              : `${isReplacing ? `Currently ${currentCores}; the worker holds ${heldCores ?? 0} of them so far.` : `Currently ${currentCores}.`}${budgetHint ? ` ${budgetHint}` : ''}`
          }
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isSubmitting || !valid || !fits}>
          {isSubmitting ? 'Resizing…' : 'Resize'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
